import { useState } from "react";
import BackButton from "../../../../components/Button/BackButton";
import CheckboxField from "../../../../components/Form/CheckboxField";
import {
  FIELD_BASE,
  FIELD_ERROR,
  fieldState,
} from "../../../../components/Form/formFieldStyles";
import { buildCodePreview } from "../../../../utils/constants/objectCodes";
import { btn } from "../../../../components/Button/buttonStyles";

const HEADERS = ["Type", "Prefix", "Code", "Code Preview"];

const ObjectCodesForm = ({ includeYear, codes, onSave, onCancel }) => {
  const [year, setYear] = useState(Boolean(includeYear));
  const [rows, setRows] = useState(
    codes.map((c) => ({
      object_type: c.object_type,
      label: c.label,
      prefix: c.prefix ?? "",
      code: String(c.code ?? ""),
    }))
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const setCell = (objectType, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.object_type === objectType ? { ...r, [field]: value } : r))
    );
    setErrors((prev) => ({ ...prev, [`${objectType}.${field}`]: "" }));
  };

  const validate = () => {
    const found = {};
    rows.forEach((r) => {
      const prefix = r.prefix.trim();
      if (!prefix) found[`${r.object_type}.prefix`] = "Required";
      else if (!/^[A-Za-z0-9]+$/.test(prefix))
        found[`${r.object_type}.prefix`] = "Letters and numbers only";
      else if (prefix.length > 10) found[`${r.object_type}.prefix`] = "Max 10 characters";

      const code = Number(r.code);
      if (r.code === "" || Number.isNaN(code)) found[`${r.object_type}.code`] = "Required";
      else if (!Number.isInteger(code) || code < 1)
        found[`${r.object_type}.code`] = "Must be 1 or higher";
    });
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    const ok = await onSave({
      include_year_in_codes: year,
      codes: rows.map((r) => ({
        object_type: r.object_type,
        prefix: r.prefix.trim().toUpperCase(),
        code: Number(r.code),
      })),
    });
    setSaving(false);
    if (ok) onCancel();
  };

  const cellInput = (row, field, extraClass = "") => {
    const key = `${row.object_type}.${field}`;
    return (
      <>
        <input
          name={key}
          value={row[field]}
          onChange={(e) => setCell(row.object_type, field, e.target.value)}
          className={`${FIELD_BASE} ${fieldState(errors[key])} text-gray-900 ${extraClass}`}
          {...(field === "code" ? { type: "number", min: 1, step: 1 } : {})}
        />
        {errors[key] && <p className={FIELD_ERROR}>{errors[key]}</p>}
      </>
    );
  };

  return (
    <div>
      <div className="mb-5 flex items-center gap-3">
        <BackButton onClick={onCancel} />
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Edit Custom Codes
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5 rounded-lg border border-gray-100 bg-white p-5 shadow-sm"
      >
        <CheckboxField
          label="Include year in codes"
          name="include_year_in_codes"
          checked={year}
          onChange={(e) => setYear(e.target.checked)}
          hint="Adds the current year between the prefix and the number, e.g. PROJ-2026-001."
        />

        <div className="overflow-x-auto border-t border-gray-100 pt-4">
          <table className="w-full min-w-[720px] table-auto border-collapse">
            <thead>
              <tr className="border-b text-[10px] uppercase tracking-widest text-gray-500">
                {HEADERS.map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.object_type} className="align-top">
                  <td className="px-3 py-2.5 text-xs font-medium text-gray-900 whitespace-nowrap">
                    {row.label}
                  </td>
                  <td className="w-40 px-3 py-2">{cellInput(row, "prefix", "uppercase")}</td>
                  <td className="w-32 px-3 py-2">{cellInput(row, "code")}</td>
                  {/* Recomputed as the prefix, code or year toggle change. */}
                  <td className="px-3 py-2.5 text-xs font-medium text-gray-900 whitespace-nowrap">
                    {buildCodePreview(row.prefix.toUpperCase(), row.code, year)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 border-t border-gray-100 pt-3">
          <span className="block text-xs text-gray-500">
            The code is the next number to be issued. Changing it does not renumber
            existing records.
          </span>
          <div className="flex gap-2 text-sm">
            <button
              type="submit"
              disabled={saving}
              className={btn("primary", "sm")}
            >
              {saving ? "Updating..." : "Update"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ObjectCodesForm;
